import { Box, Text } from '@chakra-ui/react';
import { COUNTERTOP_NONE_KEY, type DesignDetailsData, type Kitchen, useSubscription } from '@rta/rta-blueprint-library';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AppContext } from '@r-app/app-context';
import { type DesignerOptions } from '@r-core/controller';

type DesignDetailsProps = {
	options: DesignerOptions;
	kitchen: Kitchen;
};

type EditableFieldProps = {
	label: string;
	value: string;
	readonly: boolean;
	onSave: (value: string) => void;
};

const EditableField = (props: EditableFieldProps): React.ReactElement => {
	const { label, value, readonly, onSave } = props;
	const spanRef = useRef<HTMLSpanElement>(null);
	const [isEditing, setIsEditing] = useState(false);

	useEffect(() => {
		if (!isEditing && spanRef.current) {
			spanRef.current.innerText = value;
		}
	}, [value, isEditing]);

	const handleBlur = useCallback((): void => {
		setIsEditing(false);
		const newValue = spanRef.current?.innerText?.trim() ?? '';
		if (newValue !== value) {
			onSave(newValue);
		}
	}, [value, onSave]);

	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLSpanElement>): void => {
		if (e.key === 'Enter') {
			e.preventDefault();
			(e.currentTarget as HTMLSpanElement).blur();
		}
	}, []);

	const handleFocus = useCallback((): void => {
		setIsEditing(true);
	}, []);

	return (
		<Text fontSize="xs" fontWeight="bold" fontStyle="italic">
			{label}:{' '}
			<span
				ref={spanRef}
				contentEditable={!readonly}
				suppressContentEditableWarning
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				style={{
					outline: 'none',
					cursor: readonly ? 'default' : 'text',
					minWidth: '20px',
					display: 'inline'
				}}
			/>
		</Text>
	);
};

export const DesignDetails = (props: DesignDetailsProps): React.ReactElement => {
	const { options, kitchen } = props;
	const { controller } = useContext(AppContext);
	const isReadonly = controller?.readonly ?? false;

	useSubscription(controller, false, [
		['kitchen_design_details', 'updated'],
		['surround_material', 'updated'],
		['countertop_material', 'updated'],
		['countertop_thickness', 'updated'],
		['kitchen_fuel', 'updated']
	]);

	const { designDetails } = kitchen;

	const customerNameValue = designDetails?.customerName?.isCustom
		? designDetails.customerName.value
		: options.customerName;

	const panelFinishValue = designDetails?.panelFinish?.isCustom
		? designDetails.panelFinish.value
		: kitchen.material.surround.displayName;

	const countertopMaterial = kitchen.material.countertop;
	const countertopValue = designDetails?.countertop?.isCustom
		? designDetails.countertop.value
		: countertopMaterial.key === COUNTERTOP_NONE_KEY
			? `${countertopMaterial.displayName} (${countertopMaterial.thickness}")`
			: countertopMaterial.displayName;

	const fuelValue = designDetails?.fuel?.isCustom
		? designDetails.fuel.value
		: kitchen.fuelchoice?.toUpperCase();

	const designerValue = designDetails?.designer?.isCustom
		? designDetails.designer.value
		: options.designerName;

	const saveField = useCallback((field: keyof DesignDetailsData, value: string): void => {
		const current = kitchen.designDetails ?? { customerName: null, panelFinish: null, countertop: null, fuel: null };
		kitchen.designDetails = { ...current, [field]: { value, isCustom: true } };
	}, [kitchen]);

	return (
		<Box bg="#F5F5F0" p={3} borderRadius="md" border="1px solid #D5D5D0" mt={1} style={{ pointerEvents: 'auto' }}>
			<Text fontSize="sm" fontWeight="bold" textDecoration="underline" mb={1}>
				Design Details
			</Text>
			<EditableField
				label="Customer Name"
				value={customerNameValue}
				readonly={isReadonly}
				onSave={(v) => saveField('customerName', v)}
			/>
			<EditableField
				label="Panel Finish"
				value={panelFinishValue}
				readonly={isReadonly}
				onSave={(v) => saveField('panelFinish', v)}
			/>
			<EditableField
				label="Countertop"
				value={countertopValue}
				readonly={isReadonly}
				onSave={(v) => saveField('countertop', v)}
			/>
			<EditableField
				label="Fuel"
				value={fuelValue}
				readonly={isReadonly}
				onSave={(v) => saveField('fuel', v)}
			/>
			<EditableField
				label="Designer"
				value={designerValue}
				readonly={isReadonly}
				onSave={(v) => saveField('designer', v)}
			/>
		</Box>
	);
};
